import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import LaunchButtonRow from '../common/LaunchButtonRow';
import { MULTI_ADD_SCOPE } from '../../hooks/useMultiAddForms';
import { canWriteCard } from '../../utils/permissions';
import { accessContextFor } from '../../utils/collectionFamily';
import { normalizeId } from '../../utils/ids';
import { PIN_KIND } from '../../services/favorites';
import { useOptionalFavorites } from '../../contexts/FavoritesContext';

/**
 * The path a launched multi add lands on: its own page, which loads the
 * form by id (reload-safe — a pasted link works the same).
 */
export const multiAddPath = (collectionId, form) => (
	`/collections/${normalizeId(collectionId)}/multi-add/${normalizeId(form?._id)}`
);

/**
 * Navigates to a form's page. The form's name rides along in navigation
 * state so the page can title itself at once; it still works from the URL
 * alone.
 */
export function launchMultiAdd(navigate, collectionId, form) {
	navigate(multiAddPath(collectionId, form), {
		state: { multiAdd: { _id: normalizeId(form?._id), name: form?.name || '' } },
	});
}

const tierRank = scope => (scope === MULTI_ADD_SCOPE.COLLECTION ? 0 : 1);

/** The cards a form's steps create, in step order, de-duplicated. */
export function stepCardsOf(form, cardsById) {
	const seen = new Set();
	const out = [];
	(form?.steps || []).forEach(step => {
		const id = normalizeId(step?.card_id);
		if (!id || seen.has(id)) return;
		seen.add(id);
		const card = cardsById?.[id];
		if (card) out.push(card);
	});
	return out;
}

/**
 * The forms a caller may launch, ordered for a launch row: collection tier
 * first, then the caller's own, name order within each.
 *
 * A form with a step whose card the caller cannot WRITE is excluded — its
 * submit would answer 403 — and so is one with a step card the definitions
 * cache lacks (nothing to render the tile from, and a card the cache lacks
 * is one this caller cannot read).
 *
 * A sub-collection launches only its own forms: it inherits none from the
 * collections above it (owner ruling 2026-10-21, KPR-1084).
 *
 * Returns [{ form, cards, scope }].
 */
export function selectMultiAddForms({ collection, forms, cardsById }) {
	return (forms || [])
		.map(form => {
			const steps = Array.isArray(form?.steps) ? form.steps : [];
			if (!steps.length) return null;
			for (const step of steps) {
				const cardId = normalizeId(step?.card_id);
				if (!cardId || !cardsById?.[cardId]) return null;
				// A parent's form may make a sub-collection's card: the write
				// check is where that record will live.
				if (collection && !canWriteCard(accessContextFor(collection, cardId), cardId)) return null;
			}
			return {
				form,
				cards: stepCardsOf(form, cardsById),
				scope: form?.scope === MULTI_ADD_SCOPE.COLLECTION ? MULTI_ADD_SCOPE.COLLECTION : MULTI_ADD_SCOPE.USER,
			};
		})
		.filter(Boolean)
		.sort((a, b) => (
			tierRank(a.scope) - tierRank(b.scope)
			|| (a.form?.name || '').localeCompare(b.form?.name || '')
		));
}

/** "Client, Contact, Follow-up" — the cards a form creates, for tooltips and hints. */
export function describeSteps(form, cardsById) {
	return (form?.steps || []).map(step => {
		const card = cardsById?.[normalizeId(step?.card_id)];
		if (step?.repeat) return `${step.repeat.label || step.label || card?.name || 'card'} ×`;
		return step?.label || card?.name || 'card';
	}).join(', ');
}

/**
 * Multi add forms as one-click launchers (see LaunchButtonRow for the row
 * rule). The button's tile is a STACK of the cards the form creates — a
 * form has no identity of its own beyond its name — and the tooltip lists
 * them.
 *
 * `onLaunch(form)` overrides the default navigation so a host can do its own
 * housekeeping first (the Add item modal closes itself before it
 * navigates). Renders nothing when the caller has no launchable form.
 */
export default function MultiAddButtons({
	collectionId,
	collection,
	forms,
	cardsById,
	onLaunch,
	max,
	getPopupContainer,
	// Offer a Home pin toggle on each button (the collection home does; the
	// Add item modal, a place to launch from, does not).
	pinToHome = false,
}) {
	const navigate = useNavigate();
	const favoritesCtx = useOptionalFavorites();
	const canPin = pinToHome && typeof favoritesCtx?.togglePin === 'function';

	const launchable = useMemo(
		() => selectMultiAddForms({ collection, forms, cardsById }),
		[collection, forms, cardsById]
	);

	const entries = useMemo(() => launchable.map(({ form, cards, scope }) => ({
		key: normalizeId(form._id),
		pin: canPin ? {
			pinned: favoritesCtx.isPinned(PIN_KIND.MULTI_ADD, normalizeId(form._id), collectionId),
			onToggle: () => favoritesCtx.togglePin(PIN_KIND.MULTI_ADD, { targetId: normalizeId(form._id), collectionId: normalizeId(collectionId) }),
		} : undefined,
		records: cards,
		kind: 'card',
		label: form.name || '(untitled multi add)',
		scope,
		title: `Multi add · ${describeSteps(form, cardsById)}`,
		onLaunch: () => {
			if (typeof onLaunch === 'function') {
				onLaunch(form);
				return;
			}
			launchMultiAdd(navigate, collectionId, form);
		},
	})), [launchable, onLaunch, navigate, collectionId, cardsById, canPin, favoritesCtx]);

	if (!entries.length) return null;

	return (
		<LaunchButtonRow
			entries={entries}
			max={max}
			ariaLabel="Multi add"
			getPopupContainer={getPopupContainer}
		/>
	);
}
